// @flow
import ReactDOM from 'react-dom';
import logStats from 'app/common/lib/logStats';
import TimedPair from 'app/packages/time-spent/TimedPair';
import type TimeSpent from 'app/packages/time-spent/TimeSpent';

type Options = {|
  flushEvents: (sync?: boolean, resetShortOnly?: boolean, windowEvent?: { type: string }) => void,
  shouldShowLogStatements: boolean,
  track: (event: Object) => ?Object,
|};

// Object containing properties from the clickthrough event that will be
// used to create the TimedPair events.
type ClickthroughEventProperties = {
  view_type?: number,
  view_parameter?: number,
  component?: number,
  element?: number,
  object_id_str?: string,
  view_data: Object,
  aux_data: Object,
  clientTrackingParams: string,
};

const ComponentStatus = {
  ACTIVE: 'ACTIVE',
  PAUSED: 'PAUSED',
};

const getDebugTimestamp = () => {
  if (window && window.performance) {
    return `(Time: ${(window.performance.now() / 1000).toFixed(3)}s)`;
  }
  return '';
};

const once = (fn: Function) => {
  let isExecuted = false;
  return (...args: Array<any>) => {
    if (isExecuted) {
      return;
    }
    isExecuted = true;
    fn(...args);
  };
};

const createTimeSpentManager = ({
  flushEvents,
  shouldShowLogStatements = false,
  track,
}: Options) => {
  // Private store of all components
  const componentStore = new Map();

  // Wrap console methods in customConsole, which only logs stuff when
  // the URL contains a debug_timespent query string param
  const customConsole = shouldShowLogStatements
    ? console
    : {
        error: (...args) => {},
        log: (...args) => {},
        group: (...args) => {},
        groupEnd: (...args) => {},
      };

  /* eslint-disable no-console */
  // Some browsers don't have console.group
  if (!console.group) {
    // $FlowIssue
    customConsole.group = (...args) => console.log(...args);
    // $FlowIssue
    customConsole.groupEnd = (...args) => {};
  }
  /* eslint-enable no-console */

  // Store for all the data that is submitted
  // Also responsible for sending data via ContextLogging
  class TimeSpentManager {
    // TimedPair for the app
    appTimedPair: ?TimedPair = null;

    // Properties to track TimeSpent offsite for last clickthrough
    clickthroughProperties: ?ClickthroughEventProperties = null;
    offsiteTimedPair: ?TimedPair = null;
    offsiteTimedPairForegroundId: ?string = null;

    addEventListeners = () => {
      let hidden, visibilityChange;

      if (typeof document.hidden !== 'undefined') {
        // Opera 12.10 and Firefox 18 and later support
        hidden = 'hidden';
        visibilityChange = 'visibilitychange';
        // $FlowIssue: flow missing annotation for msHidden on document
      } else if (typeof document.msHidden !== 'undefined') {
        hidden = 'msHidden';
        visibilityChange = 'msvisibilitychange';
        // $FlowIssue: flow missing annotation for webkitHidden on document
      } else if (typeof document.webkitHidden !== 'undefined') {
        hidden = 'webkitHidden';
        visibilityChange = 'webkitvisibilitychange';
      }

      const handleDocumentVisibilityChange = (windowEvent: { type: string }) => {
        // $FlowIssue https://github.com/facebook/flow/issues/3146
        if (document[hidden]) {
          this.unregisterApp(false, undefined, windowEvent);
        } else if (document.hasFocus()) {
          this.registerApp();
        }
      };

      if (visibilityChange) {
        // Listen to 'visibilitychange' to capture backgrounding the browser on mobile
        document.addEventListener(visibilityChange, handleDocumentVisibilityChange, false);
      }

      window.addEventListener('focus', this.registerApp);
      window.addEventListener('blur', (windowEvent) =>
        this.unregisterApp(false, undefined, windowEvent),
      );
      window.addEventListener('beforeunload', this.handleLeave);
      // because ios safari doesn't support beforeunload (╯°□°）╯︵ ┻━┻
      window.addEventListener('pagehide', this.handleLeave);

      // This is temporary logging to detect the number of Mac Chrome users
      // that hit the browser-based back/forward buttons to navigate.
      // There is an issue where Mac Chrome triggers a blur event when
      // those navigation buttons are used.
      // https://jira.pinadmin.com/browse/MQTT-1108
      const userAgent = navigator.userAgent.toLowerCase();

      if (userAgent && userAgent.includes('macintosh') && userAgent.includes('chrome')) {
        window.addEventListener('popstate', (ev) => {
          // hasFocus should be false if the window has blurred
          // web app should be visible
          // $FlowIssue https://github.com/facebook/flow/issues/3146
          if (!document.hasFocus() && !document[hidden]) {
            logStats.increment('chrome_mac_back_forward_button', 1);
          }
        });
      }
    };

    handleLeave = once((windowEvent) => this.unregisterApp(true, undefined, windowEvent));

    /**
     * Store details of the last clickthrough so the TIMED_PAIR_BEGIN (fired on window.blur)
     * is associated with the last click.
     */
    storeClickthroughProperties = (eventProperties: ClickthroughEventProperties) => {
      if (this.clickthroughProperties) {
        logStats.increment('webapp.metrics.clickthroughs.overwritten', 1, {
          previousView: this.clickthroughProperties.view_type,
          previousViewParameter: this.clickthroughProperties.view_parameter,
          nextView: eventProperties.view_type,
          nextViewParameter: eventProperties.view_parameter,
        });
      }
      this.clickthroughProperties = eventProperties;
    };

    /**
     * Remove details of the last clickthrough event so that subsequent time spent
     * off Pinterest is not attributed to the clickthrough.
     */
    clearClickthroughProperties = () => {
      this.clickthroughProperties = null;
      this.offsiteTimedPair = null;
      this.offsiteTimedPairForegroundId = null;
    };

    /**
     * Called when the app starts, or comes back into focus.
     */
    registerApp = () => {
      customConsole.group('App Foreground', getDebugTimestamp());
      if (this.appTimedPair == null) {
        this.appTimedPair = new TimedPair();
        this.appTimedPair.start();

        this._unpauseAllComponents();

        // When the user opens/returns to Pinterest, end the "OFF_PINTEREST" TimedPair if it exists
        this._endOffsiteTimedPair();

        if (this.appTimedPair !== null && this.appTimedPair !== undefined) {
          track({
            event_type: PCONST.thrift.EventType.TIMED_APP_FOREGROUND,
            pair_id: this.appTimedPair.uuid,
          });
        }

        // TODO(tuanhuynh): browser crash fallback: start setInterval
        // to periodically update a value for timed_app_background
        // that is stored in localStorage
      } else {
        customConsole.error('TimedPair already exists for app.');
      }
      customConsole.groupEnd();
    };

    unregisterApp = (
      syncXhr: boolean = false,
      noFlush: boolean = false,
      windowEvent?: { type: string },
    ) => {
      customConsole.group('App Background', getDebugTimestamp());
      const appTimedPair = this.appTimedPair;

      if (appTimedPair !== null && appTimedPair !== undefined) {
        this._pauseAllComponents();

        const durationNs = appTimedPair.end();

        // When the user leaves Pinterest, start a TimedPair for "OFF_PINTEREST" if its a clickthrough
        this._startOffsiteTimedPair();

        track({
          event_type: PCONST.thrift.EventType.TIMED_APP_BACKGROUND,
          duration_ns: durationNs,
          pair_id: appTimedPair.uuid,
        });

        // Cleanup
        this.appTimedPair = null;

        // TODO(tuanhuynh): end the "browser crash fallback" setInterval
      } else {
        customConsole.error('No TimedPair found for app.');
      }

      // https://jira.pinadmin.com/browse/CXW-1585
      // Sometimes unregisterApp is called after context logger has flushed all events
      // from its queue. This ensures the APP BG and all TIMED_PAIR_END events are sent.
      if (!noFlush) {
        flushEvents(syncXhr, undefined, windowEvent);
      }

      customConsole.groupEnd();
    };

    _activateComponent = (timeSpentComponent: TimeSpent) => {
      customConsole.group('Activating component', getDebugTimestamp());
      if (shouldShowLogStatements) {
        customConsole.log(ReactDOM.findDOMNode(timeSpentComponent));
      }

      const timedPair = timeSpentComponent.getTimedPair();
      if (timedPair) {
        timedPair.start();

        const {
          view,
          viewParameter,
          component,
          element,
          objectId,
          clientTrackingParams,
        } = timeSpentComponent.getLoggerContext();

        track({
          event_type: PCONST.thrift.EventType.TIMED_PAIR_BEGIN,
          view_type: view,
          view_parameter: viewParameter,
          component,
          element,
          object_id_str: objectId,
          view_data: timeSpentComponent.props.viewData,
          pair_id: timedPair.uuid,
          clientTrackingParams,
          aux_data: {
            // auxData
            time_spent_foreground_pairid: this.appTimedPair && this.appTimedPair.uuid,
            time_spent_parent_pairid: timeSpentComponent.getParentId(),
            ...timeSpentComponent.props.auxData,
          },
        });
      }

      customConsole.groupEnd();
    };

    _deactivateComponent = (timeSpentComponent: TimeSpent) => {
      customConsole.group('Deactivating component', getDebugTimestamp());
      if (shouldShowLogStatements) {
        customConsole.log(ReactDOM.findDOMNode(timeSpentComponent));
      }

      const timedPair = timeSpentComponent.getTimedPair();
      if (timedPair) {
        const {
          view,
          viewParameter,
          component,
          element,
          objectId,
          clientTrackingParams,
        } = timedPair;

        const durationNs = timedPair.end();

        track({
          event_type: PCONST.thrift.EventType.TIMED_PAIR_END,
          view_type: view,
          view_parameter: viewParameter,
          component,
          element,
          object_id_str: objectId,
          view_data: timeSpentComponent.props.viewData,
          duration_ns: durationNs,
          pair_id: timedPair.uuid,
          clientTrackingParams,
          aux_data: {
            // auxData
            time_spent_foreground_pairid: this.appTimedPair && this.appTimedPair.uuid,
            time_spent_parent_pairid: timeSpentComponent.getParentId(),
            ...timeSpentComponent.props.auxData,
          },
        });
      }

      customConsole.groupEnd();
    };

    /**
     * Create and log an "OFF_PINTEREST" TimedPair begin event.
     */
    _startOffsiteTimedPair = () => {
      const { clickthroughProperties } = this;

      if (clickthroughProperties) {
        // Store deets about the timed pair so we can end it in _endOffsiteTimedPair
        this.offsiteTimedPair = new TimedPair({
          ...clickthroughProperties,
          view: PCONST.thrift.ViewType.OFF_PINTEREST,
          viewParameter: clickthroughProperties.view_parameter,
          objectId: clickthroughProperties.object_id_str,
        });
        this.offsiteTimedPair.start();
        this.offsiteTimedPairForegroundId = this.appTimedPair ? this.appTimedPair.uuid : null;

        track({
          ...clickthroughProperties,
          event_type: PCONST.thrift.EventType.TIMED_PAIR_BEGIN,
          view_type: PCONST.thrift.ViewType.OFF_PINTEREST,
          pair_id: this.offsiteTimedPair ? this.offsiteTimedPair.uuid : null,
          aux_data: {
            ...clickthroughProperties.aux_data,
            time_spent_foreground_pairid: this.offsiteTimedPairForegroundId,
          },
        });
      }
    };

    /**
     * If an "OFF_PINTEREST" TimedPair was created, end it and log the event.
     */
    _endOffsiteTimedPair = () => {
      const { offsiteTimedPair, clickthroughProperties } = this;

      if (offsiteTimedPair && clickthroughProperties) {
        const durationNs = offsiteTimedPair.end();

        track({
          ...clickthroughProperties,
          event_type: PCONST.thrift.EventType.TIMED_PAIR_END,
          view_type: PCONST.thrift.ViewType.OFF_PINTEREST,
          pair_id: this.offsiteTimedPair ? this.offsiteTimedPair.uuid : null,
          aux_data: {
            ...clickthroughProperties.aux_data,
            time_spent_foreground_pairid: this.offsiteTimedPairForegroundId,
          },
          duration_ns: durationNs,
        });

        this.clearClickthroughProperties();
      }
    };

    _pauseAllComponents = () => {
      customConsole.group('Pause all components');

      componentStore.forEach((status, component, map) => {
        if (status === ComponentStatus.ACTIVE) {
          this._deactivateComponent(component);
        }

        map.set(component, ComponentStatus.PAUSED);
      });

      customConsole.groupEnd();
    };

    _unpauseAllComponents = () => {
      customConsole.group('Unpause all components');

      // Ensure each component has a new TimedPair (and UUID) before activating.
      // Otherwise, we may activate a child before a TimedPair is created for its parent.
      componentStore.forEach((status, component) => {
        component.setTimedPair(new TimedPair(component.getLoggerContext()));
      });

      componentStore.forEach((status, component) => {
        componentStore.set(component, ComponentStatus.ACTIVE);
        this._activateComponent(component);
      });

      customConsole.groupEnd();
    };

    registerComponent(timeSpentComponent: TimeSpent) {
      // If app is not registered, set component status to paused
      if (this.appTimedPair === null) {
        componentStore.set(timeSpentComponent, ComponentStatus.PAUSED);
      } else {
        componentStore.set(timeSpentComponent, ComponentStatus.ACTIVE);
        this._activateComponent(timeSpentComponent);
      }
    }

    unregisterComponent(timeSpentComponent: TimeSpent) {
      const status = componentStore.get(timeSpentComponent);

      if (status === ComponentStatus.ACTIVE) {
        this._deactivateComponent(timeSpentComponent);

        // set a new timed pair to ensure a unique ID is used when component is re-activated.
        timeSpentComponent.setTimedPair(new TimedPair(timeSpentComponent.getLoggerContext()));
      }

      componentStore.delete(timeSpentComponent);
    }

    size() {
      return componentStore.size;
    }
  }

  return new TimeSpentManager();
};

// Export the instance
export default createTimeSpentManager;
